'use client';

import { ReactElement, useMemo, useRef } from 'react';
import Image from 'next/image';
import classNames from 'classnames';
import { planName, PlanType } from '@lib/billing-plan-name';
import { Tooltip } from '@nx-frontend/shared/ui/tooltip';
import { WorkspaceImage } from '@nx-frontend/shared/ui/workspace-image';
import { ButtonProps } from '@nx-frontend/shared/types';
import { UiTooltipPosition } from '@nx-frontend/shared/types';

export function NavbarButton({
  icon,
  buttonID,
  image,
  tooltipContent,
  additionalTooltipContent,
  isSelected,
  onClick,
  trialDaysLeft,
  orgButtonActive,
  tooltipPosition,
  tooltipMessageClasses,
  defaultCursor,
}: ButtonProps) {
  const buttonRef = useRef(null);

  let additionalTooltipEl: ReactElement | null | string = null;
  if (additionalTooltipContent) {
    additionalTooltipEl = <span>{additionalTooltipContent}</span>;
  }

  const baseButtonStyles = 'flex justify-center items-center relative w-full h-11 py-1.5';
  const selectedButtonStyles = 'border-[3px] border-transparent border-r-teal-900 border-solid';
  const isCursorDefault = (orgButtonActive === false && buttonID === 'org') || defaultCursor;
  const imageElement = useMemo(() => {
    if (image) {
      if (buttonID === 'user') {
        return <img src={image} className="rounded-full object-cover w-full h-full" alt="" />;
      } else {
        return <WorkspaceImage image={image} />;
      }
    } else {
      return icon;
    }
  }, [buttonID, icon, image]);

  const buttonElement = (
    <button
      data-cy={buttonID}
      ref={buttonRef}
      onClick={onClick(buttonID)}
      className={classNames(baseButtonStyles, {
        [selectedButtonStyles]: isSelected,
        'cursor-default': isCursorDefault,
        'cursor-pointer hover:bg-neutral-100': !isCursorDefault,
        'beamerTrigger': buttonID === 'beamer',
      })}
    >
      {'upgrade-trial' === buttonID && trialDaysLeft ? (
        <div className="group-tooltip relative flex">
          <div
            className={classNames('flex justify-center items-center relative w-8 h-8', {
              'hover:bg-neutral-100 hover:rounded cursor-pointer': !defaultCursor,
            })}
          >
            {image ? <WorkspaceImage image={image} /> : icon}
          </div>
          <div
            className="flex flex-col items-center absolute z-[1000] opacity-0 invisible [.group-tooltip:hover_&]:visible rounded-[4px] shadow-md [.group-tooltip:hover_&]:opacity-100 transition-all delay-1000 duration-300 p-1 shadow-black-300 scale-100 w-max top-[50%] translate-y-[-50%] right-[-10px] translate-x-[100%] p-[10px] border"
            style={{
              border: '1px solid var(--Light-theme-Border-Main, #E5E5E5)',
              background: '#fff',
            }}
          >
            <div className="text-[14px] font-bold">
              Your trial ends in {trialDaysLeft} {1 < trialDaysLeft ? 'days' : 'day'}
            </div>
            <Image
              src="/img/upgrade-trial-tooltip.png"
              width={163}
              height={68}
              alt="Portal"
              className="pt-[8px] pb-[8px]"
            />
            <div className="w-[163px] text-[12px] text-left">
              Upgrade now to keep your {planName[PlanType.BUSINESS]} perks. Don't miss out!
            </div>
          </div>
        </div>
      ) : (
        <div
          className={
            'ai-agents' === buttonID
              ? classNames('flex justify-center items-center relative w-[52px] h-[60px]')
              : classNames('flex justify-center items-center relative w-8 h-8')
          }
        >
          {imageElement}
        </div>
      )}
    </button>
  );

  return (
    <>
      {tooltipContent && (
        <div className="w-full">
          <Tooltip
            message={tooltipContent}
            messageClasses={'w-full'}
            position={tooltipPosition ?? UiTooltipPosition.RIGHT}
            additionalMessage={additionalTooltipEl}
            show={!('upgrade-trial' === buttonID && trialDaysLeft)}
          >
            {buttonElement}
          </Tooltip>
        </div>
      )}
      {!tooltipContent && buttonElement}
    </>
  );
}
